import THREE from "@phdsoft/three";

export class ThreeJsCleanup {
  /**
   * Disposes a Three.js object, including its children, geometry, materials, and self.
   * Removes the object from its parent and calls its dispose method (if it exists).
   */
  static disposeObject(object: any): void {
      if (!object) return;

      // Recursively dispose of children
      if (object.children && object.children.length > 0) {
        [...object.children].forEach((child) => this.disposeObject(child));
      }

      // Remove the object from its parent
      if (object.parent) {
          object.parent.remove(object);
      }

      // Dispose geometry if it exists
      if (object.geometry) {
          object.geometry.dispose();
      }

      // Dispose material(s) using the helper method
      if (object.material) {
          if (Array.isArray(object.material)) {
              object.material.forEach((material) => this.disposeMaterial(material));
          } else {
              this.disposeMaterial(object.material);
          }
      }

      // Call dispose method if the object has one
      if (typeof object.dispose === 'function') {
          object.dispose();
      }
  }

  /**
   * Disposes a material and its associated textures.
   */
  static disposeMaterial(material: THREE.Material): void {
      if (!material) return;

      // Dispose all associated textures (maps)
      const textureProps = ['map', 'lightMap', 'bumpMap', 'normalMap', 'specularMap', 'envMap', 'alphaMap'];
      textureProps.forEach((prop) => {
          if ((material as any)[prop]) {
              (material as any)[prop].dispose();
          }
      });

      // Dispose the material itself
      material.dispose();
  }

  /**
   * Disposes a renderer.
   */
  static disposeRenderer(renderer: THREE.WebGLRenderer): void {
      if (renderer) renderer.dispose();
  }

  /**
   * Disposes a control object.
   */
  static disposeControls(controls: any): void {
      if (controls && typeof controls.dispose === 'function') {
          controls.dispose();
      }
  }

  /**
   * Cancels an animation frame.
   */
  static cancelAnimation(requestId: number): void {
      if (requestId) cancelAnimationFrame(requestId);
  }
}
